import React, { useEffect, useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useController, useFormContext } from 'react-hook-form';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import classNames from 'classnames';

import classes from './RecipeImageField.module.scss';
import {
  getFileFormatsText,
  getFileRejectionMessage,
} from '../../../../../shared/utils/fileUtils';
import { getBytesFromMb } from '../../../../../shared/utils/mathUtils';
import { RecipeFormValues } from '../../interfaces/RecipeFormValues';

type RecipeImageFieldProps = {
  /** the loaded recipe's image URL */
  savedImageUrl: string | null;
};

const MAX_FILE_SIZE_MB = 5;
const ACCEPTED_FILE_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};
const FORMATS_TEXT = getFileFormatsText(Object.values(ACCEPTED_FILE_TYPES).flat());

const RecipeImageField = ({ savedImageUrl }: RecipeImageFieldProps) => {
  const { control } = useFormContext<RecipeFormValues>();
  const {
    field: { value: file, onChange },
  } = useController({ control, name: 'image' });

  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setFileUrl(null);
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setFileUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleDropRejected = ([rejectedFile]: FileRejection[]) => {
    const [{ code }] = rejectedFile.errors;
    const message = getFileRejectionMessage(code, {
      formatsText: FORMATS_TEXT,
      maxFileSizeMb: MAX_FILE_SIZE_MB,
    });

    enqueueSnackbar(message, { variant: 'error' });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    maxSize: getBytesFromMb(MAX_FILE_SIZE_MB),
    accept: ACCEPTED_FILE_TYPES,
    onDropAccepted: ([acceptedFile]) => onChange(acceptedFile),
    onDropRejected: handleDropRejected,
  });

  const imageUrl = fileUrl ?? savedImageUrl;

  return (
    <div {...getRootProps({ className: classes.thumbnail })}>
      {imageUrl ? (
        <img src={imageUrl} alt="Recipe" />
      ) : (
        <CameraAltRoundedIcon color="white" fontSize="inherit" />
      )}
      <label
        className={classNames(classes.upload, { [classes.dragging]: isDragActive })}
        data-test-id="upload-recipe-image"
      >
        <CameraAltRoundedIcon color="white" fontSize="inherit" />
        <input {...getInputProps()} />
      </label>
    </div>
  );
};

export { RecipeImageField };
